import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Message, DirectMessage } from '@sahaj/types';
import {
  formatMessageTime,
} from '@sahaj/utils';
import {
  Plus,
  MessageSquareReply,
  Edit3,
  Trash2,
  Check,
  X,
  Globe,
  Copy,
  Loader2,
  ChevronDown,
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useLanguage, TRANSLATION_TARGET_LANGUAGES } from '../../hooks/useLanguage';
import { api } from '../../lib/api';
import type { TranslateResponse } from '@sahaj/types';

import { MessageRenderer } from './MessageRenderer';
import { EmojiPicker } from './EmojiPicker';
import { ReactionDetailsPopup } from './ReactionDetailsPopup';
import { QUICK_REACTION_EMOJIS } from './emojiData';
import { UserAvatar } from '../common/UserAvatar';

interface MessageItemProps {
  message: Message | DirectMessage;
  isThreadParent?: boolean;
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
}

export const MessageItem: React.FC<MessageItemProps> = ({
  message,
  isThreadParent,
  isFirstInGroup = true,
}) => {
  const { user } = useAuth();
  const { members } = useWorkspace();
  const {
    t,
    preferredTranslationLanguage,
    autoTranslate,
    getLanguageName,
  } = useLanguage();

  const {
    openThread,
    editMessage,
    deleteMessage,
    addReaction,
    removeReaction,
    activeChat,
  } = useChat();

  // Build UUID->displayName map for mention resolution
  const membersMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const m of members) {
      if (m.user?.id && m.user?.displayName) {
        map[m.user.id] = m.user.displayName;
      }
    }
    return map;
  }, [members]);
  const [searchParams] =
    useSearchParams();

  const isHighlighted =
    searchParams.get('messageId') ===
    message.id;

  const [isEditing, setIsEditing] =
    useState(false);

  const [editContent, setEditContent] =
    useState(message.content || '');

  const [showFullEmojiPicker, setShowFullEmojiPicker] =
    useState(false);

  const [selectedReactionEmoji, setSelectedReactionEmoji] =
    useState<string | null>(null);
  const [hoveredReactionEmoji, setHoveredReactionEmoji] =
    useState<string | null>(null);

  // ─── Translation State ──────────────────────────────────────────────────────
  const [translatedResult, setTranslatedResult] =
    useState<TranslateResponse | null>(null);
  const [isTranslating, setIsTranslating] =
    useState(false);
  const [translationError, setTranslationError] =
    useState<string | null>(null);
  const [showOriginal, setShowOriginal] =
    useState(false);
  const [copiedTranslation, setCopiedTranslation] =
    useState(false);
  const [showLangPicker, setShowLangPicker] =
    useState(false);

  const isChannelMessage =
    activeChat?.type === 'channel';

  const isAuthor =
    message.sender.id === user?.id;

  const isDeleted =
    !!message.deletedAt;

  const reactions =
    (message as Message).reactions || [];

  const replyCount =
    (message as Message).replyCount || 0;

  const handleSaveEdit = async () => {
    if (!editContent.trim()) {
      return;
    }

    await editMessage(
      message.id,
      editContent
    );

    setIsEditing(false);
  };

  const handleReactionClick = (
    emoji: string,
    hasReacted: boolean
  ) => {
    if (hasReacted) {
      removeReaction(
        message.id,
        emoji
      );
    } else {
      addReaction(
        message.id,
        emoji
      );
    }
  };

  const getReactionTooltipText = (reaction: { emoji: string; count: number; userIds?: string[]; hasReacted?: boolean }) => {
    const userIds = reaction.userIds || [];
    const isSelf = user?.id ? userIds.includes(user.id) || !!reaction.hasReacted : !!reaction.hasReacted;

    const names: string[] = [];
    userIds.forEach((uid) => {
      if (user && uid === user.id) return;
      const member = members.find((m) => m.user.id === uid);
      if (member) {
        names.push(member.user.displayName);
      }
    });

    if (isSelf) {
      names.unshift('You');
    }

    if (names.length === 0) {
      return `${reaction.count} ${reaction.count === 1 ? 'person' : 'people'}`;
    }

    if (names.length === 1) {
      return `${names[0]}`;
    } else if (names.length === 2) {
      return `${names[0]} and ${names[1]}`;
    } else if (names.length === 3) {
      return `${names[0]}, ${names[1]}, and ${names[2]}`;
    } else {
      return `${names.slice(0, 3).join(', ')} and ${names.length - 3} other${names.length - 3 === 1 ? '' : 's'}`;
    }
  };

  const handleAddReactionFromPicker = (
    emoji: string
  ) => {
    const existing = reactions.find((r) => r.emoji === emoji);
    const isSelf = existing ? (existing.userIds && user?.id ? existing.userIds.includes(user.id) : !!existing.hasReacted) : false;
    handleReactionClick(
      emoji,
      isSelf
    );

    setShowFullEmojiPicker(false);
  };

  // ─── Translation Handlers ───────────────────────────────────────────────────
  const handleTranslate = async (targetLang?: string) => {
    const langToUse = targetLang || preferredTranslationLanguage || 'hi';
    if (!message.content || !message.content.trim()) return;

    try {
      setIsTranslating(true);
      setTranslationError(null);
      const res = await api.translation.translate({
        messageId: message.id,
        text: message.content,
        targetLanguage: langToUse,
      });
      setTranslatedResult(res);
      setShowOriginal(false);
    } catch (err: any) {
      console.error('Translation error:', err);
      setTranslationError(t('chat.translationFailed'));
    } finally {
      setIsTranslating(false);
    }
  };

  const handleCopyTranslation = async () => {
    if (!translatedResult?.translatedText) return;
    try {
      await navigator.clipboard.writeText(translatedResult.translatedText);
      setCopiedTranslation(true);
      setTimeout(() => setCopiedTranslation(false), 2000);
    } catch {
      // ignore
    }
  };

  // Auto-translate incoming messages if enabled
  React.useEffect(() => {
    if (
      autoTranslate &&
      !isAuthor &&
      message.content &&
      message.content.trim() &&
      !translatedResult &&
      !isTranslating
    ) {
      handleTranslate(preferredTranslationLanguage);
    }
  }, [autoTranslate, isAuthor, message.id, message.content]);

  if (isDeleted) {
    return (
      <div className="flex justify-start px-4 my-1.5">
        <span className="px-3 py-1 text-xs italic text-muted-foreground/70 bg-muted/30 rounded-full border border-border/40 inline-flex items-center gap-1.5">
          <Trash2 className="h-3 w-3" />

          <span>
            This message was deleted
          </span>
        </span>
      </div>
    );
  }

  // Spacing between message groups vs consecutive
  // messages from the same sender.
  const groupSpacing =
    isFirstInGroup
      ? 'mt-3.5'
      : 'mt-0.5';

  return (
    <div
      id={`msg-${message.id}`}
      data-message-id={message.id}
      className={`group relative flex gap-3 px-3.5 py-1.5 rounded-2xl transition-colors duration-150 ${groupSpacing} items-start hover:bg-muted/40 dark:hover:bg-muted/20 ${
        isHighlighted
          ? 'bg-primary/10 ring-2 ring-primary/40'
          : ''
      }`}
    >
      {/* Avatar — visible only on first message in group */}
      {isFirstInGroup ? (
        <UserAvatar
          userId={message.sender.id}
          displayName={message.sender.displayName}
          avatarUrl={message.sender.avatarUrl}
          size="md"
          className="shadow-2xs mt-0.5 shrink-0"
        />
      ) : (
        <div className="w-10 flex-shrink-0" />
      )}

      {/* Message Content & Meta Wrapper */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Header: Sender Name & Timestamp */}
        {isFirstInGroup && (
          <div className="flex items-center gap-2 mb-1 text-[13px] leading-none">
            <span className="font-bold text-foreground hover:underline cursor-pointer">
              {message.sender.displayName}
            </span>

            <span className="text-xs text-muted-foreground">
              {formatMessageTime(
                message.createdAt
              )}
            </span>

            {message.isEdited && (
              <span className="text-[11px] text-muted-foreground/80 italic">
                ({t('chat.edited')})
              </span>
            )}
          </div>
        )}

        {/* Message Bubble & Toolbar Container */}
        <div className="relative">
          {isEditing ? (
            <div className="p-3 bg-card border border-border rounded-xl shadow-md space-y-2 max-w-2xl my-1">
              <textarea
                value={editContent}
                onChange={(e) =>
                  setEditContent(
                    e.target.value
                  )
                }
                className="w-full p-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 leading-relaxed font-sans"
                rows={3}
              />

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setEditContent(
                      message.content ||
                        ''
                    );

                    setIsEditing(false);
                  }}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-muted text-muted-foreground text-xs font-semibold rounded-lg hover:bg-muted/80 transition"
                >
                  <X className="h-3 w-3" />
                  {t('common.cancel')}
                </button>

                <button
                  type="button"
                  onClick={
                    handleSaveEdit
                  }
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold rounded-lg hover:bg-primary/90 transition shadow-sm"
                >
                  <Check className="h-3 w-3" />
                  {t('common.save')}
                </button>
              </div>
            </div>
          ) : (
            <div className="relative text-foreground select-text max-w-full text-left">
              {/* Message Bubble Card Box */}
              <div
                className={`relative rounded-2xl border border-border/70 bg-card/60 dark:bg-card/40 backdrop-blur-2xs px-4 py-3 shadow-2xs transition-all duration-150 group-hover:border-border group-hover:bg-card/90 group-hover:shadow-xs ${
                  isHighlighted
                    ? 'border-primary/60 bg-primary/[0.08] ring-2 ring-primary/30'
                    : ''
                }`}
              >
                <MessageRenderer
                  content={
                    message.content || ''
                  }
                  membersMap={membersMap}
                />
              </div>
            </div>
          )}

                {/* Translation Loading Bar */}
                {isTranslating && (
                  <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-primary/[0.06] border border-primary/20 text-xs text-primary font-medium animate-pulse">
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                    <span>{t('chat.translating')}</span>
                  </div>
                )}

                {/* Translation Error */}
                {translationError && (
                  <div className="mt-2 flex items-center justify-between gap-3 px-3 py-1.5 rounded-xl bg-destructive/10 border border-destructive/20 text-xs text-destructive">
                    <span>{translationError}</span>
                    <button
                      type="button"
                      onClick={() => handleTranslate()}
                      className="font-bold underline hover:opacity-80 transition cursor-pointer"
                    >
                      Retry
                    </button>
                  </div>
                )}

                {/* Translated Message Box */}
                {translatedResult && !isTranslating && (
                  <div className="mt-2 p-3 rounded-2xl bg-muted/40 dark:bg-card/90 border border-primary/25 shadow-xs text-xs space-y-2 animate-in fade-in zoom-in-95 duration-150 select-text max-w-2xl">
                    {/* Header Bar */}
                    <div className="flex items-center justify-between gap-2 pb-1.5 border-b border-border/70">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Globe className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                        <span className="font-bold text-foreground text-[11px] truncate">
                          {t('chat.translationHeader', {
                            lang: getLanguageName(translatedResult.targetLanguage),
                          })}
                        </span>
                        {translatedResult.cached && (
                          <span className="text-[9px] font-mono px-1.5 py-0.2 rounded-full bg-primary/10 text-primary font-bold">
                            {t('chat.cachedTranslation')}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1 flex-shrink-0">
                        {/* Language Switcher Dropdown */}
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setShowLangPicker(!showLangPicker)}
                            title={t('chat.selectTargetLanguage')}
                            aria-label={t('chat.selectTargetLanguage')}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-card hover:bg-muted border border-border/70 text-[11px] font-medium text-foreground transition"
                          >
                            <span>{translatedResult.targetLanguage.toUpperCase()}</span>
                            <ChevronDown className="h-3 w-3 text-muted-foreground" />
                          </button>

                          {showLangPicker && (
                            <div className="absolute right-0 top-full mt-1 w-36 py-1 bg-popover border border-border rounded-xl shadow-xl z-30 max-h-48 overflow-y-auto">
                              {TRANSLATION_TARGET_LANGUAGES.map((lang) => (
                                <button
                                  key={lang.code}
                                  type="button"
                                  onClick={() => {
                                    setShowLangPicker(false);
                                    handleTranslate(lang.code);
                                  }}
                                  className={`w-full text-left px-2.5 py-1.5 text-xs flex items-center gap-2 hover:bg-muted transition ${
                                    translatedResult.targetLanguage === lang.code
                                      ? 'font-bold text-primary bg-primary/10'
                                      : 'text-foreground'
                                  }`}
                                >
                                  <span>{lang.flag}</span>
                                  <span className="truncate">{lang.nativeName}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Copy Translation */}
                        <button
                          type="button"
                          onClick={handleCopyTranslation}
                          title={t('common.copy')}
                          aria-label={t('common.copy')}
                          className="p-1 hover:bg-muted text-muted-foreground hover:text-foreground rounded-lg transition"
                        >
                          {copiedTranslation ? (
                            <Check className="h-3.5 w-3.5 text-emerald-500" />
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                        </button>

                        {/* Show Original / Show Translation Toggle */}
                        <button
                          type="button"
                          onClick={() => setShowOriginal(!showOriginal)}
                          className="px-2 py-0.5 rounded-lg text-[11px] font-semibold text-primary hover:bg-primary/10 transition"
                        >
                          {showOriginal
                            ? t('chat.showTranslation')
                            : t('chat.showOriginal')}
                        </button>

                        {/* Dismiss Translation */}
                        <button
                          type="button"
                          onClick={() => setTranslatedResult(null)}
                          title="Close translation"
                          aria-label="Close translation"
                          className="p-1 hover:bg-muted text-muted-foreground hover:text-foreground rounded-lg transition"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Body Content */}
                    <div className="text-foreground leading-relaxed pt-0.5">
                      {!showOriginal ? (
                        <p className="whitespace-pre-wrap font-normal text-xs leading-relaxed text-foreground">
                          {translatedResult.translatedText}
                        </p>
                      ) : (
                        <p className="whitespace-pre-wrap italic text-muted-foreground text-xs leading-relaxed">
                          {message.content}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

        {/* Reaction Badges with Reaction Details Popup */}
        {reactions.length > 0 && (
          <div className="relative flex flex-wrap items-center gap-1.5 mt-2">
            {/* Reaction Details Popup */}
            {(() => {
              const activeReaction = reactions.find(
                (reaction) => reaction.emoji === selectedReactionEmoji
              );

              if (!activeReaction) {
                return null;
              }

              return (
                <ReactionDetailsPopup
                  emoji={activeReaction.emoji}
                  count={activeReaction.count}
                  userIds={activeReaction.userIds}
                  hasReacted={activeReaction.hasReacted}
                  onClose={() => setSelectedReactionEmoji(null)}
                  onToggleReaction={() =>
                    handleReactionClick(
                      activeReaction.emoji,
                      activeReaction.hasReacted
                    )
                  }
                />
              );
            })()}

            {reactions.map((reaction) => {
              const isSelf = user?.id ? (reaction.userIds || []).includes(user.id) || !!reaction.hasReacted : !!reaction.hasReacted;
              return (
                <div
                  key={reaction.emoji}
                  className="relative inline-flex"
                  onMouseEnter={() => setHoveredReactionEmoji(reaction.emoji)}
                  onMouseLeave={() => setHoveredReactionEmoji(null)}
                >
                  <button
                    type="button"
                    onClick={() => handleReactionClick(reaction.emoji, isSelf)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setSelectedReactionEmoji((previous) =>
                        previous === reaction.emoji ? null : reaction.emoji
                      );
                    }}
                    title={`${getReactionTooltipText(reaction)} • Click to ${isSelf ? 'remove' : 'add'} reaction (Right-click for details)`}
                    aria-label={`${reaction.count} people reacted with ${reaction.emoji}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs border transition-all duration-150 cursor-pointer select-none ${
                      isSelf
                        ? 'bg-primary/15 border-primary/50 text-primary font-bold shadow-xs scale-[1.02]'
                        : 'bg-card/90 border-border/80 text-foreground hover:bg-muted/80 hover:border-border shadow-2xs hover:scale-105 active:scale-95'
                    } ${
                      selectedReactionEmoji === reaction.emoji
                        ? 'ring-2 ring-primary/50'
                        : ''
                    }`}
                  >
                    <span className="text-sm leading-none">{reaction.emoji}</span>
                    <span className="font-bold text-xs text-primary dark:text-primary-foreground">{reaction.count}</span>
                  </button>

                  {/* Instant Hover Tooltip */}
                  {hoveredReactionEmoji === reaction.emoji && !selectedReactionEmoji && (
                    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-xl bg-slate-900 text-white text-[11px] font-medium shadow-xl border border-slate-700/60 pointer-events-none whitespace-nowrap z-40 animate-in fade-in zoom-in-95 duration-100 flex items-center gap-1.5">
                      <span>{reaction.emoji}</span>
                      <span>{getReactionTooltipText(reaction)}</span>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Quick add reaction button in reactions row */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowFullEmojiPicker((prev) => !prev)}
                title="Add reaction"
                aria-label="Add reaction"
                className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-border/80 bg-card/60 hover:bg-muted text-muted-foreground hover:text-foreground transition cursor-pointer text-xs shadow-2xs"
              >
                <Plus className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}

        {/* Thread replies button */}
        {!isThreadParent && isChannelMessage && replyCount > 0 && (
          <button
            type="button"
            onClick={() => openThread(message as Message)}
            className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-primary hover:underline group/thread w-fit"
          >
            <MessageSquareReply className="h-3.5 w-3.5" />
            <span>
              {replyCount === 1
                ? t('chat.singleReply')
                : t('chat.replyCount', { count: replyCount })}
            </span>
          </button>
        )}
      </div>

      {/* Floating Hover Action Toolbar */}
      {!isEditing && (
        <div className="absolute -top-3.5 right-4 z-30 flex items-center gap-0.5 bg-card/95 backdrop-blur-sm border border-border shadow-lg rounded-xl p-1 text-card-foreground opacity-0 group-hover:opacity-100 focus-within:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-all duration-150 transform translate-y-1 group-hover:translate-y-0">
          {/* Quick reactions bar */}
          <div className="flex items-center gap-0.5 border-r border-border/70 pr-1 mr-0.5">
            {QUICK_REACTION_EMOJIS.map((emoji) => {
              const existing = reactions.find((r) => r.emoji === emoji);
              const isSelf = existing ? (existing.userIds && user?.id ? existing.userIds.includes(user.id) : !!existing.hasReacted) : false;
              return (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => handleReactionClick(emoji, isSelf)}
                  title={`React with ${emoji}${isSelf ? ' (Click to remove)' : ''}`}
                  aria-label={`React with ${emoji}`}
                  className={`p-1 rounded-lg text-sm transition-all duration-150 hover:scale-125 active:scale-95 cursor-pointer ${
                    isSelf ? 'bg-primary/20 ring-1 ring-primary/40 shadow-xs' : 'hover:bg-muted'
                  }`}
                >
                  {emoji}
                </button>
              );
            })}

            {/* Plus button — full emoji picker */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowFullEmojiPicker((prev) => !prev)}
                title="Add Reaction"
                aria-label="Add Reaction"
                className="p-1.5 hover:bg-muted text-muted-foreground hover:text-foreground rounded-lg transition"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>

              {showFullEmojiPicker && (
                <EmojiPicker
                  onSelect={handleAddReactionFromPicker}
                  onClose={() => setShowFullEmojiPicker(false)}
                  align="right"
                  side="top"
                />
              )}
            </div>
          </div>

          {/* Translate Action Button */}
          <button
            type="button"
            onClick={() => handleTranslate()}
            title={t('chat.translate')}
            aria-label={t('chat.translate')}
            className="p-1.5 hover:bg-primary/10 text-muted-foreground hover:text-primary rounded-lg transition"
          >
            <Globe className="h-3.5 w-3.5" />
          </button>

          {/* Reply in Thread */}
          {!isThreadParent && isChannelMessage && (
            <button
              type="button"
              onClick={() => openThread(message as Message)}
              title="Reply in thread"
              aria-label="Reply in thread"
              className="p-1.5 hover:bg-muted text-muted-foreground hover:text-foreground rounded-lg transition"
            >
              <MessageSquareReply className="h-3.5 w-3.5" />
            </button>
          )}

          {/* Edit */}
          {isAuthor && (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              title="Edit message"
              aria-label="Edit message"
              className="p-1.5 hover:bg-muted text-muted-foreground hover:text-foreground rounded-lg transition"
            >
              <Edit3 className="h-3.5 w-3.5" />
            </button>
          )}

          {/* Delete */}
          {isAuthor && (
            <button
              type="button"
              onClick={() => deleteMessage(message.id)}
              title="Delete message"
              aria-label="Delete message"
              className="p-1.5 hover:bg-destructive/10 text-muted-foreground hover:text-destructive rounded-lg transition"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};